import useRolePermission from "entities/auth/hooks/useRolePermission";
import {
  invalidateMetadataTagDetailsQuery,
  useMetadataTagDetailsQuery,
} from "entities/metadataTags/queries/useMetadataTagDetailsQuery";
import { invalidateMetadataTagListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { useUpdateMetadataTagQuery } from "entities/metadataTags/queries/useUpdateMetadataTagQuery";
import { MetadataTagToUpdate } from "entities/metadataTags/types";
import React, { FC, useEffect, useState } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useMetadataTagsPage } from "../../../context";
import {
  MetadataTagDetailsTabs,
  MetadataTagDetailsProviderProps,
  MetadataTagDetailsType,
} from "./types";

const [Provider, useMetadataTagDetails] = createContext<MetadataTagDetailsType>(
  {
    name: "MetadataTagDetailsContext",
    hookName: "useMetadataTagDetails",
    providerName: "MetadataTagDetailsProvider",
  },
);

const MetadataTagDetailsProvider: FC<MetadataTagDetailsProviderProps> = ({
  children,
}) => {
  const { allowed: hasReadAccess } = useRolePermission("tags:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "tags:translations:read",
  );
  const { metadataTagDetailsDrawer } = useMetadataTagsPage();
  const { drawerDetails } = metadataTagDetailsDrawer;
  const [selectedTab, setSelectedTab] = useState(
    MetadataTagDetailsTabs.General,
  );
  const [metadataTagId, setMetadataTagId] = useState<string>();
  const [metadataTagTitle, setMetadataTagTitle] = useState<string>("");
  const [metadataSecondaryTags, setMetadataSecondaryTags] = useState<string[]>(
    [],
  );
  const metadataTagName: InputHookResult = useInputState("metadataTagName", "");
  const metadataTagDescription: InputHookResult = useInputState(
    "metadataTagDescription",
    "",
  );
  const [allowTranslationSecondaryTags, setAllowTranslationSecondaryTags] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<string>("");
  const [translations, setTranslations] = useState<Translations | undefined>();

  const { data: metadataTagData, isLoading } =
    useMetadataTagDetailsQuery(drawerDetails || "");
  const { mutate: updateMetadataTagDetails, isPending: isSubmittingTag } =
    useUpdateMetadataTagQuery();

  useEffect(() => {
    if (metadataTagData) {
      setMetadataTagId(metadataTagData.id);
      setMetadataTagTitle(metadataTagData.primaryTag);
      metadataTagName.setValue(metadataTagData.primaryTag);
      metadataTagDescription.setValue(metadataTagData.description || "");

      setMetadataSecondaryTags(metadataTagData.secondaryTags || []);
      setTranslations(metadataTagData.translations);
      setAllowTranslationSecondaryTags(metadataTagData.type !== "free_input");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataTagData, metadataTagData?.id]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(MetadataTagDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess]);

  const onUpdateMetadataTagDetails = async (
    metadataTagId: string,
    metadataTag: MetadataTagToUpdate,
  ) => {
    return updateMetadataTagDetails({ metadataTagId, metadataTag });
  };

  const onSubmitMetadataTag = async () => {
    try {
      const metadataTagToSubmit = {
        description: metadataTagDescription?.input.value,
        translations: translations || {},
        updatedAt: new Date(),
      };

      if (metadataTagId) {
        await onUpdateMetadataTagDetails(metadataTagId, metadataTagToSubmit);
        invalidateMetadataTagDetailsQuery(metadataTagId);
      }

      // Invalidate query
      metadataTagDetailsDrawer.closeDrawer();
      setMetadataTagId(undefined);
      invalidateMetadataTagListQuery();
    } catch (e: any) {
      console.error(e);
    }
  };

  const onSubmitMetadataTagTranslation = async () => {
    try {
      const metadataTagToSubmit = {
        translations: translations || {},
        updatedAt: new Date(),
      };
      if (metadataTagId) {
        await onUpdateMetadataTagDetails(metadataTagId, metadataTagToSubmit);
        invalidateMetadataTagDetailsQuery(metadataTagId);
      }

      // Invalidate query
      metadataTagDetailsDrawer.closeDrawer();
      setMetadataTagId(undefined);
      invalidateMetadataTagListQuery();
    } catch (e: any) {
      console.error(e);
    }
  };

  const onCloseTagDetails = () => {
    metadataTagDetailsDrawer.closeDrawer();
    setSelectedTab(MetadataTagDetailsTabs.General);
    setMetadataTagTitle("");
    setAllowTranslationSecondaryTags(false);
  };

  const value = {
    loading: isLoading,
    submitting: isSubmittingTag,
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    errors,
    metadataTagId,
    metadataTagTitle,
    metadataTagName,
    metadataSecondaryTags,
    metadataTagDescription,
    allowTranslationSecondaryTags,
    onSubmitMetadataTag,
    onSubmitMetadataTagTranslation,
    setErrors,
    onCloseTagDetails,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { MetadataTagDetailsProvider, useMetadataTagDetails };
